import { useLingui } from "@lingui/react/macro";
import { DAILY_BUDGET_MAX_USD } from "@rakazo/contracts";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@rakazo/ui-web";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useState } from "react";
import { useSpaceQueries } from "../lib/query";
import { rpc } from "../lib/rpc";

const draftOf = (value: number | null) => (value == null ? "" : String(value));

/**
 * A daily cap in USD that saves on Enter or when it loses focus. Empty clears it, Escape undoes
 * the edit, and an amount the server would refuse is marked invalid, with why, instead of sent.
 * `zeroNote` says what a cap of $0 does, shown while the cap is 0.
 */
export function DailyCapInput({
  value,
  placeholder,
  label,
  onSave,
  max = DAILY_BUDGET_MAX_USD,
  zeroNote,
}: {
  value: number | null;
  placeholder: string;
  label: string;
  onSave: (value: number | null) => Promise<void>;
  max?: number;
  zeroNote?: string;
}) {
  const { t, i18n } = useLingui();
  const noteId = useId();
  const [draft, setDraft] = useState(draftOf(value));
  const [invalid, setInvalid] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setDraft(draftOf(value));
    setInvalid(false);
  }, [value]);

  async function commit() {
    const text = draft.trim();
    const next = text === "" ? null : Number(text);
    if (next !== null && !(Number.isFinite(next) && next >= 0 && next <= max)) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    if (next === value || saving) return;
    setSaving(true);
    try {
      await onSave(next);
    } catch {
      setInvalid(true);
    } finally {
      setSaving(false);
    }
  }

  const most = new Intl.NumberFormat(i18n?.locale).format(max);
  const note = invalid
    ? t`Enter 0 to ${most}`
    : zeroNote && draft.trim() !== "" && Number(draft) === 0
      ? zeroNote
      : null;
  return (
    <div className="flex flex-col items-end gap-1">
      <InputGroup className="h-8 w-28">
        <InputGroupAddon>$</InputGroupAddon>
        <InputGroupInput
          aria-label={label}
          aria-invalid={invalid || undefined}
          aria-describedby={note ? noteId : undefined}
          inputMode="decimal"
          autoComplete="off"
          className="tabular-nums"
          value={draft}
          placeholder={placeholder}
          disabled={saving}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={() => void commit()}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              void commit();
            } else if (event.key === "Escape") {
              setDraft(draftOf(value));
              setInvalid(false);
            }
          }}
        />
      </InputGroup>
      {note ? (
        <p
          id={noteId}
          role={invalid ? "alert" : undefined}
          className={`max-w-48 text-end text-[12px] leading-snug ${invalid ? "text-destructive" : "text-muted-foreground"}`}
        >
          {note}
        </p>
      ) : null}
    </div>
  );
}

/** Saves caps and refreshes the spend view that shows them. */
export function useCapSavers() {
  const queries = useSpaceQueries();
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: queries.usage.bots.key() });
  return {
    saveBot: async (botId: string, dailyBudgetUsd: number | null) => {
      await rpc.budgets.setBot({ botId, dailyBudgetUsd });
      await refresh();
    },
    saveSpaceDefault: async (dailyBudgetUsd: number | null) => {
      await rpc.budgets.setSpaceDefault({ dailyBudgetUsd });
      await refresh();
    },
  };
}

/** A bot's own cap, edited from its settings; empty follows the Space default. */
export function BotDailyCap({ botId }: { botId: string }) {
  const { t } = useLingui();
  const queries = useSpaceQueries();
  const { data } = useQuery(queries.usage.bots.queryOptions());
  const { saveBot } = useCapSavers();
  const row = data?.bots.find((bot) => bot.botId === botId);
  if (!data || !row) return null;
  return (
    <div className="flex min-h-11 items-center justify-between gap-3 text-sm text-foreground">
      <span>{t`Daily cap`}</span>
      <DailyCapInput
        value={row.ownDailyBudgetUsd}
        placeholder={capPlaceholder(data.spaceDefaultDailyBudgetUsd, t`None`)}
        label={t`Daily cap`}
        zeroNote={t`At $0 this Nexa Mate won't run.`}
        onSave={(value) => saveBot(botId, value)}
      />
    </div>
  );
}

/** An empty bot cap shows what it follows: the Space default, else none. */
export function capPlaceholder(spaceDefault: number | null, none: string): string {
  return spaceDefault == null ? none : String(spaceDefault);
}
